import { KeyboardArrowDownRounded, KeyboardArrowRightRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Fragment, useState } from "react";
import { useOrgScheduleDetail } from "@/hooks/useSubscriptionSchedules";
import {
  REVENUE_SCHEDULE_KIND_LABEL,
  type ScheduleCycleDetail,
} from "@/interfaces/subscriptionSchedules";
import { buildCsv, type CsvColumn, downloadCsv } from "@/lib/csv";
import { formatDate } from "@/utils/date-format";
import {
  BackLink,
  card,
  cell,
  Fact,
  money,
  num,
  periodLabel,
  SectionHeading,
  shortDay,
  tableBox,
  th,
} from "./shared";

interface LineCsvRow {
  sequence: number;
  invoice: string;
  period: string;
  days: number;
  amount: string;
  journalNo: string;
  postedOn: string;
}

const LINE_CSV: CsvColumn<LineCsvRow>[] = [
  { label: "Cycle", value: (r) => String(r.sequence) },
  { label: "Invoice", value: (r) => r.invoice },
  { label: "Month", value: (r) => r.period },
  { label: "Days", value: (r) => String(r.days) },
  { label: "Amount", value: (r) => r.amount },
  { label: "Journal entry", value: (r) => r.journalNo },
  { label: "Released", value: (r) => r.postedOn },
];

function CycleLines({
  cycle,
  onOpenEntry,
}: {
  cycle: ScheduleCycleDetail;
  onOpenEntry: (journalEntryId: number) => void;
}) {
  const posted = cycle.lines.filter((l) => l.journalEntryId !== null).length;
  const [firstDue] = cycle.lines.filter((l) => l.journalEntryId === null);
  return (
    <Table
      size="small"
      sx={{
        border: "1px solid var(--color-card-border)",
        borderRadius: "8px",
        background: "var(--color-white)",
      }}
    >
      <TableHead>
        <TableRow>
          <TableCell sx={th}>Month</TableCell>
          <TableCell sx={{ ...th, textAlign: "right" }}>Days</TableCell>
          <TableCell sx={{ ...th, textAlign: "right" }}>Amount</TableCell>
          <TableCell sx={th}>Released</TableCell>
          <TableCell sx={th}>Journal entry</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {cycle.lines.map((l) => {
          const due = l.journalEntryId === null;
          const next = l === firstDue;
          const style = due && !next ? { color: "var(--color-graphite)", fontStyle: "italic" } : {};
          return (
            <TableRow
              key={l.period}
              sx={next ? { "& td": { background: "var(--color-good-ochre-bg)" } } : undefined}
            >
              <TableCell sx={{ ...cell, ...style, fontWeight: next ? 700 : 400 }}>
                {periodLabel(l.period)}
              </TableCell>
              <TableCell sx={{ ...num, ...style }}>{l.days}</TableCell>
              <TableCell sx={{ ...num, ...style }}>{money(l.amount)}</TableCell>
              <TableCell sx={{ ...cell, ...style }}>
                {due ? `Due ${shortDay(l.dueOn)}` : formatDate(l.postedOn)}
              </TableCell>
              <TableCell sx={{ ...cell, ...style }}>
                {l.journalEntryId !== null ? (
                  <Link
                    component="button"
                    type="button"
                    onClick={() => onOpenEntry(l.journalEntryId as number)}
                    sx={{ fontSize: 13, color: "var(--color-rodeo-black)" }}
                  >
                    {l.journalNo}
                  </Link>
                ) : (
                  "—"
                )}
              </TableCell>
            </TableRow>
          );
        })}
        <TableRow>
          <TableCell sx={{ ...cell, fontWeight: 700 }}>Total</TableCell>
          <TableCell />
          <TableCell sx={{ ...num, fontWeight: 700 }}>{money(cycle.amount)}</TableCell>
          <TableCell colSpan={2} sx={{ ...cell, color: "var(--color-graphite)" }}>
            {posted} released · {cycle.lines.length - posted} to come
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}

/**
 * One organization's subscription deferral: what is still owed in service, its billing cycles, and
 * each cycle's month lines with the entry that posted them. The active cycle opens expanded.
 */
export default function OrgScheduleDetail({
  organizationId,
  fy,
  onBack,
  onOpenEntry,
}: {
  organizationId: number;
  fy: number;
  onBack: () => void;
  onOpenEntry: (journalEntryId: number) => void;
}) {
  const { data, isLoading, isError } = useOrgScheduleDetail(organizationId, fy);
  const [expanded, setExpanded] = useState<Set<number> | null>(null);

  if (isLoading) {
    return <Typography sx={{ color: "var(--color-graphite)" }}>Loading…</Typography>;
  }
  if (isError || !data) {
    return (
      <Box>
        <BackLink label="Deferred Subscriptions" onClick={onBack} />
        <Typography sx={{ mt: 2, color: "var(--color-graphite)" }}>
          This organization has no subscription revenue schedules.
        </Typography>
      </Box>
    );
  }

  // Until someone toggles, the active cycles are open and the completed ones closed.
  const open =
    expanded ?? new Set(data.cycles.filter((c) => c.status === "active").map((c) => c.scheduleId));
  const toggle = (id: number) => {
    const next = new Set(open);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  };
  const active = data.cycles.filter((c) => c.status === "active").length;

  const exportLines = () => {
    const rows: LineCsvRow[] = data.cycles.flatMap((c) =>
      c.lines.map((l) => ({
        sequence: c.sequence,
        invoice: c.stripeInvoiceNumber ?? "",
        period: l.period,
        days: l.days,
        amount: l.amount,
        journalNo: l.journalNo ?? "",
        postedOn: l.postedOn ?? "",
      })),
    );
    downloadCsv(`deferred-subscriptions-org-${data.organizationId}.csv`, buildCsv(LINE_CSV, rows));
  };

  return (
    <Box>
      <BackLink label="Deferred Subscriptions" onClick={onBack} />
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          mt: 1,
          mb: 2.5,
        }}
      >
        <Box>
          <Typography component="h1" sx={{ fontSize: 22, fontWeight: 700 }}>
            {data.organizationName}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
            {data.planName ?? "No plan"}
            {data.billingInterval
              ? ` · ${data.billingInterval === "year" ? "annual" : "monthly"}`
              : ""}
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          onClick={exportLines}
          sx={{
            textTransform: "none",
            borderColor: "var(--color-dark-dust)",
            color: "var(--color-dust-ink)",
          }}
        >
          Export lines CSV
        </Button>
      </Box>

      <Box
        component="section"
        aria-label="Summary"
        sx={{
          ...card,
          mb: 3.5,
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, minmax(0, 1fr))" },
          gap: 4,
        }}
      >
        <Fact label="Still owed">{money(data.remaining)}</Fact>
        <Fact label={`Delivered in FY ${data.fiscalYear}`}>{money(data.recognisedInYear)}</Fact>
        <Fact label="Next release">
          {data.nextPeriod ? (
            <>
              {money(data.nextAmount)}{" "}
              <Typography component="span" sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
                {periodLabel(data.nextPeriod)}
              </Typography>
            </>
          ) : (
            "—"
          )}
        </Fact>
        <Fact label="Billing periods">
          {data.cycles.length}{" "}
          <Typography component="span" sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
            {active} active · {data.cycles.length - active} completed
          </Typography>
        </Fact>
      </Box>

      <SectionHeading title="Billing periods" />
      <Box sx={tableBox}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...th, width: 40 }} />
              <TableCell sx={th}>#</TableCell>
              <TableCell sx={th}>Service term</TableCell>
              <TableCell sx={th}>Invoice</TableCell>
              <TableCell sx={th}>Kind</TableCell>
              <TableCell sx={{ ...th, textAlign: "right" }}>Paid</TableCell>
              <TableCell sx={{ ...th, textAlign: "right" }}>Delivered to date</TableCell>
              <TableCell sx={{ ...th, textAlign: "right" }}>Still owed</TableCell>
              <TableCell sx={th}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.cycles.map((c) => {
              const isOpen = open.has(c.scheduleId);
              return (
                <Fragment key={c.scheduleId}>
                  <TableRow
                    sx={isOpen ? { "& td": { background: "var(--color-clear-dust)" } } : undefined}
                  >
                    <TableCell sx={cell}>
                      <IconButton
                        size="small"
                        aria-label={`${isOpen ? "Collapse" : "Expand"} cycle ${c.sequence}`}
                        aria-expanded={isOpen}
                        onClick={() => toggle(c.scheduleId)}
                      >
                        {isOpen ? (
                          <KeyboardArrowDownRounded fontSize="small" />
                        ) : (
                          <KeyboardArrowRightRounded fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={cell}>{c.sequence}</TableCell>
                    <TableCell sx={cell}>
                      {formatDate(c.serviceStart)} – {formatDate(c.serviceEnd)}
                    </TableCell>
                    <TableCell sx={cell}>
                      {c.hostedInvoiceUrl ? (
                        <Link href={c.hostedInvoiceUrl} target="_blank" rel="noreferrer">
                          {c.stripeInvoiceNumber ?? "View"}
                        </Link>
                      ) : (
                        (c.stripeInvoiceNumber ?? "—")
                      )}
                    </TableCell>
                    <TableCell sx={cell}>{REVENUE_SCHEDULE_KIND_LABEL[c.kind]}</TableCell>
                    <TableCell sx={num}>{money(c.amount)}</TableCell>
                    <TableCell sx={num}>{money(c.recognised)}</TableCell>
                    <TableCell sx={{ ...num, fontWeight: 700 }}>{money(c.remaining)}</TableCell>
                    <TableCell sx={cell}>
                      {c.status === "active" ? (
                        <Chip size="small" color="success" variant="outlined" label="Active" />
                      ) : (
                        <Chip size="small" label="Completed" />
                      )}
                    </TableCell>
                  </TableRow>
                  {isOpen && (
                    <TableRow>
                      <TableCell
                        colSpan={9}
                        sx={{ background: "var(--color-clear-dust)", pl: 7, pb: 2 }}
                      >
                        <CycleLines cycle={c} onOpenEntry={onOpenEntry} />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}
